import type { InviteWire } from './group-chat-actions';
import type { GroupMember } from './group-chat';
import { UNRESOLVED, type PickedRecipient } from './recipient-field';
import type { PickedContact } from './contact-references';
import { waGroupCardStateKey, type WaGroupRoster } from './wa-group-roster';

/**
 * The Maestro's group-setup card (`metadata.groupSetupCard` on an ASSISTANT row — contract "Group setup + WhatsApp-group
 * roster", 2026-11-02). The Maestro no longer asks for a group's name, members or numbers in text: it posts ONE card, and
 * "Review and create" opens the New group dialog PREFILLED from it. Once the dialog created the group, the server writes
 * `created` onto the same row and the card becomes the group's live status card.
 *
 * Pure rules only — the card's reader, the dialog's prefill, the two create bodies and the per-member state lines — so the
 * three surfaces can hold the same decisions (iOS `GroupSetupCard`, Android `GroupSetupCard`). Mirrors common-lib's
 * `readGroupSetupCard`; kept here, like `readInviteCard`, so web reads the wire without waiting on a common-lib build.
 */

export type GroupSetupKind = 'SYMPHONY' | 'WHATSAPP';

export interface GroupSetupCardMember {
  name: string;
  /** WHATSAPP invitees go by this. */
  recipientId?: string;
  /** SYMPHONY invitees go by this. */
  email?: string;
  /** False: this kind has no way to reach them (no WhatsApp number / no email). Absent reads as true. */
  canInvite?: boolean;
}

export interface GroupSetupCardData {
  setupId: string;
  kind: GroupSetupKind;
  name: string;
  fromGroupConversationId?: string;
  members: GroupSetupCardMember[];
  created?: { groupConversationId: string; waGroupId?: string; kind?: 'SYMPHONY' | 'WHATSAPP' };
}

/** The card row the server writes `created` onto: the private chat it sits in, and its persisted message id. */
export interface SetupCardRef {
  conversationId: string;
  messageId: string;
}

/** The New WhatsApp group invitee cap: 7 people, 8 with the owner (the server refuses more with `too_many_invitees`). */
export const WA_GROUP_SETUP_MAX_INVITEES = 7;
/** The whole group, the owner included — the "Up to {count} people in a group, including you." hint. */
export const WA_GROUP_SETUP_MAX_PEOPLE = WA_GROUP_SETUP_MAX_INVITEES + 1;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Read `metadata.groupSetupCard`. Fail closed: an unknown kind, or a card with no name and no members, is no card. */
export function readGroupSetupCard(metadata: unknown): GroupSetupCardData | null {
  const raw = isObject(metadata) ? metadata.groupSetupCard : undefined;
  if (!isObject(raw)) return null;
  const kind = text(raw.kind);
  if (kind !== 'SYMPHONY' && kind !== 'WHATSAPP') return null;
  const members = (Array.isArray(raw.members) ? raw.members : []).flatMap((m): GroupSetupCardMember[] => {
    if (!isObject(m)) return [];
    const name = text(m.name);
    const email = text(m.email);
    if (!name && !email) return [];
    return [{
      name: name || email,
      ...(text(m.recipientId) ? { recipientId: text(m.recipientId) } : {}),
      ...(email ? { email } : {}),
      ...(typeof m.canInvite === 'boolean' ? { canInvite: m.canInvite } : {}),
    }];
  });
  const name = text(raw.name);
  if (!name && members.length === 0) return null;
  const madeKind = isObject(raw.created) ? text(raw.created.kind).toUpperCase() : '';
  const created: GroupSetupCardData['created'] | null = isObject(raw.created) && text(raw.created.groupConversationId)
    ? {
        groupConversationId: text(raw.created.groupConversationId),
        ...(text(raw.created.waGroupId) ? { waGroupId: text(raw.created.waGroupId) } : {}),
        ...(madeKind === 'SYMPHONY' || madeKind === 'WHATSAPP' ? { kind: madeKind } : {}),
      }
    : null;
  return {
    setupId: text(raw.setupId),
    // What was made wins over what was suggested: the dialog can switch a WhatsApp card to a Symphony group, and back.
    kind: created?.kind ?? kind,
    name,
    ...(text(raw.fromGroupConversationId) ? { fromGroupConversationId: text(raw.fromGroupConversationId) } : {}),
    members,
    ...(created ? { created } : {}),
  };
}

/** The kind line's (and the segmented control's) label key. */
export function groupSetupKindKey(kind: GroupSetupKind): string {
  return kind === 'WHATSAPP' ? 'chat.groupSetup.kind.whatsapp' : 'chat.groupSetup.kind.symphony';
}

/**
 * Whether this kind can reach the member. The card's own `canInvite: false` is the server's word for the card's kind;
 * after the reader switches kind in the dialog, the address that kind needs decides (an email for Symphony, a recipient
 * for WhatsApp).
 */
export function memberReachable(member: GroupSetupCardMember, kind: GroupSetupKind, cardKind: GroupSetupKind): boolean {
  if (kind === cardKind && member.canInvite === false) return false;
  return kind === 'SYMPHONY' ? !!member.email : !!member.recipientId;
}

/** The note under an unreachable member's name ("No WhatsApp number" / "No email"); null when they can be invited. */
export function memberNoteKey(member: GroupSetupCardMember, kind: GroupSetupKind, cardKind: GroupSetupKind = kind): string | null {
  if (memberReachable(member, kind, cardKind)) return null;
  return kind === 'WHATSAPP' ? 'chat.groupSetup.member.noWhatsapp' : 'chat.groupSetup.member.noEmail';
}

/** One person in the WhatsApp dialog: a card member (by recipient) or a contact picked there (by phone). */
export interface WaSetupInvitee {
  key: string;
  name: string;
  recipientId?: string;
  phone?: string;
}

export interface GroupSetupPrefill {
  kind: GroupSetupKind;
  name: string;
  /** The Symphony recipient field's chips: every card member with an email. */
  symphonyPicked: PickedRecipient[];
  /** The WhatsApp list: every card member with a recipient, capped at 7. */
  waInvitees: WaSetupInvitee[];
}

/**
 * What "Review and create" opens the dialog with — the card's name and its reachable members, for both kinds. With
 * Symphony group chat off for this reader (`symphonyGroupChat`), there is no Symphony option: a SYMPHONY card opens the
 * WhatsApp side.
 */
export function groupSetupPrefill(card: GroupSetupCardData, symphonyAvailable = true): GroupSetupPrefill {
  return {
    kind: symphonyAvailable ? card.kind : 'WHATSAPP',
    name: card.name,
    symphonyPicked: card.members
      .filter((m) => memberReachable(m, 'SYMPHONY', card.kind))
      .map((m) => ({
        ...(m.recipientId ? { id: m.recipientId } : {}),
        name: m.name,
        identifier: m.email as string,
        membership: UNRESOLVED,
        isContact: true,
      })),
    waInvitees: card.members
      .filter((m) => memberReachable(m, 'WHATSAPP', card.kind))
      .slice(0, WA_GROUP_SETUP_MAX_INVITEES)
      .map((m) => ({ key: `r:${m.recipientId}`, name: m.name, recipientId: m.recipientId })),
  };
}

/** The card members this kind cannot reach — drawn in the dialog with their note, never invited. */
export function unreachableMembers(card: GroupSetupCardData, kind: GroupSetupKind): GroupSetupCardMember[] {
  return card.members.filter((m) => !memberReachable(m, kind, card.kind));
}

/**
 * A contact picked in the WhatsApp dialog: by its first phone. Null for a contact with no phone (nothing to invite by)
 * or one already in the list (same phone, or the same name as a card member — the card member already carries them).
 */
export function waInviteeFromContact(contact: PickedContact, current: readonly WaSetupInvitee[]): WaSetupInvitee | null {
  const phone = contact.phones.map((p) => p.trim()).find(Boolean);
  if (!phone) return null;
  const digits = (v: string) => v.replace(/\D/g, '');
  if (current.some((i) => (i.phone && digits(i.phone) === digits(phone)) || (!!contact.id && i.key === `c:${contact.id}`))) return null;
  return { key: contact.id ? `c:${contact.id}` : `p:${digits(phone)}`, name: contact.name.trim() || phone, phone };
}

/** The `POST /api/whatsapp-groups` body for a create from the setup dialog; null when it is not sendable (no name, nobody, more than 7). */
export function waGroupCreateBody(input: { subject: string; invitees: readonly WaSetupInvitee[]; setupCard?: SetupCardRef | null }): Record<string, unknown> | null {
  const subject = input.subject.trim();
  if (!subject || input.invitees.length === 0 || input.invitees.length > WA_GROUP_SETUP_MAX_INVITEES) return null;
  return {
    subject,
    invitees: input.invitees.map((i) => ({
      ...(i.recipientId ? { recipientId: i.recipientId } : {}),
      ...(i.name ? { name: i.name } : {}),
      ...(i.phone ? { phone: i.phone } : {}),
    })),
    ...(input.setupCard ? { setupCard: { conversationId: input.setupCard.conversationId, messageId: input.setupCard.messageId } } : {}),
  };
}

export type NotInvitedReason = 'no_whatsapp' | 'not_on_card' | 'unresolved';
export interface NotInvited { name: string; reason: NotInvitedReason }

/** The create answer's `notInvited` list, unknown reasons read as `unresolved`. */
export function parseNotInvited(raw: unknown): NotInvited[] {
  const list = isObject(raw) && Array.isArray(raw.notInvited) ? raw.notInvited : [];
  return list.flatMap((entry): NotInvited[] => {
    if (!isObject(entry)) return [];
    const name = text(entry.name);
    if (!name) return [];
    const reason = entry.reason === 'no_whatsapp' || entry.reason === 'not_on_card' ? entry.reason : 'unresolved';
    return [{ name, reason }];
  });
}

/** "Arava · No WhatsApp number" for a person the create could not invite. */
export function notInvitedNoteKey(reason: NotInvitedReason): string {
  return reason === 'no_whatsapp' ? 'chat.groupSetup.member.noWhatsapp' : 'chat.groupSetup.state.failed';
}

/** One line of the created card: a person and their state key (null while the state is not known yet). */
export interface SetupMemberLine { name: string; stateKey: string | null }

/**
 * A created SYMPHONY group's people: who joined (from `members`, never the viewer) and whose invite is pending. Before the
 * `members` read answers, the card's own members are listed without a state.
 */
export function symphonyMemberLines(card: GroupSetupCardData, read: { members: GroupMember[]; pendingInvites: InviteWire[] } | null): SetupMemberLine[] {
  if (!read) return card.members.map((m) => ({ name: m.name, stateKey: null }));
  const joined = read.members.filter((m) => !m.you).map((m) => ({ name: m.name.trim() || '—', stateKey: 'chat.groupSetup.state.joined' }));
  const invited = read.pendingInvites.map((i) => ({ name: i.name.trim() || i.email, stateKey: 'chat.groupSetup.state.invited' }));
  return [...joined, ...invited];
}

/** A created WHATSAPP group's people, from the chat list's roster; the card's members without a state until it answers. */
export function whatsappMemberLines(card: GroupSetupCardData, roster: WaGroupRoster | null): SetupMemberLine[] {
  if (!roster) return card.members.filter((m) => memberReachable(m, 'WHATSAPP', card.kind)).map((m) => ({ name: m.name, stateKey: null }));
  return roster.members.map((m) => ({ name: m.name, stateKey: waGroupCardStateKey(m.state) }));
}

/** The Maestro's card id for this row: the persisted id (`finalId` on a row that streamed in this session). */
export function setupCardRef(conversationId: string | null | undefined, message: { id?: string; finalId?: string }): SetupCardRef | null {
  const messageId = message.finalId ?? message.id;
  return conversationId && messageId ? { conversationId, messageId } : null;
}

// "Re-read this chat": the dialog closes after a create, and the server has written `created` onto the card row — the
// transcript re-reads its window so the same card turns into the status card. The chat surface subscribes.
const reloadListeners = new Set<(conversationId: string) => void>();
export function onSetupChatReloadRequested(listener: (conversationId: string) => void): () => void {
  reloadListeners.add(listener);
  return () => { reloadListeners.delete(listener); };
}
export function requestSetupChatReload(conversationId: string): void {
  reloadListeners.forEach((l) => l(conversationId));
}
